import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import type { SessionTenantPayload, SessionUserPayload } from '@/lib/api/session';
import { useSession } from '@/lib/hooks/useSession';
import { supabase } from '@/integrations/supabase/client';
import {
  clientRoleToTenantRole,
  resolveClientTenantRole,
  resolveRolePermissions as resolveTenantRolePermissions,
} from '@/lib/auth/roles';
import { useTenantStore } from '@/stores/tenantStore';

export type StaffRole =
  | 'PLATFORM_ADMIN'
  | 'PAYROLL_ADMIN'
  | 'OWNER'
  | 'MANAGER'
  | 'CASHIER'
  | 'COOK'
  | 'EXPO'
  | 'SUPPORT'
  | 'EMPLOYEE';

interface StaffInfo {
  id: string;
  restaurant_id: string;
  role: StaffRole;
  name: string;
  email: string;
  is_active: boolean;
}

const ROLE_TIMEOUT_MS = 1_500;

const KNOWN_ROLES: StaffRole[] = [
  'PLATFORM_ADMIN',
  'PAYROLL_ADMIN',
  'OWNER',
  'MANAGER',
  'CASHIER',
  'COOK',
  'EXPO',
  'SUPPORT',
  'EMPLOYEE',
];

interface FallbackStaffParams {
  userId: string;
  email?: string | null;
  restaurantId?: string | null;
  restaurantName?: string | null | undefined;
  role: StaffRole;
}

const deriveRole = (role: string | null | undefined, hasTenant: boolean): StaffRole => {
  const resolved = resolveClientTenantRole(role);
  if (resolved && KNOWN_ROLES.includes(resolved as StaffRole)) {
    return resolved as StaffRole;
  }

  return hasTenant ? 'SUPPORT' : 'SUPPORT';
};

const deriveDisplayName = (email?: string | null, fallbackName?: string | null  ): string => {
  if (fallbackName && fallbackName.length > 0) {
    return fallbackName;
  }

  if (email && email.length > 0) {
    const [prefix] = email.split('@');
    if (prefix) {
      return prefix;
    }
  }

  return 'Owner';
};

const buildFallbackStaffInfo = ({
  userId,
  email,
  restaurantId,
  restaurantName,
  role,
}: FallbackStaffParams): StaffInfo => ({
  id: `fallback-${restaurantId ?? userId}`,
  restaurant_id: restaurantId ?? `tenant-${userId}`,
  role,
  name: deriveDisplayName(email, restaurantName),
  email: email ?? '',
  is_active: true,
});

const mergePermissions = (role: StaffRole, permissions: unknown, hasTenant: boolean): string[] => {
  const normalized = new Set<string>();

  if (Array.isArray(permissions)) {
    permissions.forEach((permission) => {
      if (typeof permission === 'string' && permission.length > 0) {
        normalized.add(permission);
      }
    });
  }

  if (hasTenant) {
    const tenantRole = clientRoleToTenantRole(role);
    resolveTenantRolePermissions(tenantRole).forEach((permission) => normalized.add(permission));
  }

  return Array.from(normalized);
};

const isFallbackStaff = (info: StaffInfo | null): boolean => !info || info.id.startsWith('fallback-');

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
};

const isSessionUserPayload = (value: unknown): value is SessionUserPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    typeof candidate.role === 'string'
  );
};

const isSessionTenantPayload = (value: unknown): value is SessionTenantPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.slug === 'string' &&
    (candidate.name === undefined || candidate.name === null || typeof candidate.name === 'string')
  );
};

interface SessionSnapshot {
  authenticated: boolean;
  user: SessionUserPayload | null;
  tenant: SessionTenantPayload | null;
  permissions: string[];
  loading: boolean;
}

const extractSessionSnapshot = (value: unknown): SessionSnapshot => {
  if (!value || typeof value !== 'object') {
    return {
      authenticated: false,
      user: null,
      tenant: null,
      permissions: [],
      loading: false,
    };
  }

  const record = value as Record<string, unknown>;
  const rawUser = 'user' in record ? record.user : undefined;
  const rawTenant = 'tenant' in record ? record.tenant : undefined;
  const rawPermissions = 'permissions' in record ? record.permissions : undefined;

  return {
    authenticated: Boolean(record.authenticated),
    user: isSessionUserPayload(rawUser) ? rawUser : null,
    tenant: isSessionTenantPayload(rawTenant) ? rawTenant : null,
    permissions: toStringArray(rawPermissions),
    loading: Boolean(record.loading),
  };
};

interface UseRoleResult {
  staffInfo: StaffInfo | null;
  role: StaffRole;
  permissions: string[];
  loading: boolean;
  hasRole: (requiredRole: StaffRole | StaffRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  refreshStaffInfo: () => Promise<void>;
  isPlatformAdmin: boolean;
  isOwner: boolean;
  isManager: boolean;
  isCashier: boolean;
  isCook: boolean;
  isExpo: boolean;
  isSupport: boolean;
  isKitchen: boolean;
  isWaiter: boolean;
}

export const useRole = (): UseRoleResult => {
  const { user: authUser, loading: authLoading } = useAuth();
  const sessionResult: unknown = useSession();
  const {
    authenticated: sessionAuthenticated,
    user: sessionUser,
    tenant: sessionTenant,
    permissions: sessionPermissions,
    loading: sessionLoading,
  } = extractSessionSnapshot(sessionResult);
  const { activeRestaurantId, activeRestaurantName } = useTenantStore();

  const [staffInfo, setStaffInfo] = useState<StaffInfo | null>(null);
  const [role, setRole] = useState<StaffRole>(deriveRole(sessionUser?.role ?? null, Boolean(sessionTenant)));
  const [permissions, setPermissions] = useState<string[]>(mergePermissions(role, sessionPermissions, Boolean(sessionTenant)));
  const [roleResolved, setRoleResolved] = useState(false);
  const staffFetchInFlightRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const loadStaffInfo = useCallback(async () => {
    if (!authUser) {
      return null;
    }

    const { data, error } = await supabase
      .from('staff')
      .select(
        `
        id,
        restaurant_id,
        role,
        name,
        email,
        is_active
      `,
      )
      .eq('auth_user_id', authUser.id)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data as StaffInfo | null;
  }, [authUser]);

  useEffect(() => () => clearTimeoutRef(), [clearTimeoutRef]);

  useEffect(() => {
    if (sessionLoading) {
      setRoleResolved(false);
      return;
    }

    const hasTenant = Boolean(sessionTenant);
    const normalizedRole = deriveRole(sessionUser?.role ?? null, hasTenant);
    const normalizedPermissions = mergePermissions(normalizedRole, sessionPermissions, hasTenant);

    setRole((current) => (current === normalizedRole ? current : normalizedRole));
    setPermissions((current) => {
      const next = normalizedPermissions;
      if (current.length === next.length && current.every((item, index) => item === next[index])) {
        return current;
      }
      return next;
    });
    setRoleResolved(true);

    if (!sessionAuthenticated) {
      setStaffInfo(null);
      return;
    }

    if (sessionUser && sessionTenant) {
      setStaffInfo((current) => {
        if (current && !isFallbackStaff(current)) {
          if (
            current.role === normalizedRole &&
            current.restaurant_id === sessionTenant.id &&
            current.email === (sessionUser.email ?? current.email)
          ) {
            return current;
          }

          return {
            ...current,
            role: normalizedRole,
            restaurant_id: sessionTenant.id,
            email: sessionUser.email ?? current.email,
            name: current.name ?? deriveDisplayName(sessionUser.email, activeRestaurantName),
          };
        }

        const fallback = buildFallbackStaffInfo({
          userId: sessionUser.id,
          email: sessionUser.email,
          restaurantId: sessionTenant.id,
          restaurantName: activeRestaurantName,
          role: normalizedRole,
        });

        if (
          current &&
          isFallbackStaff(current) &&
          current.restaurant_id === fallback.restaurant_id &&
          current.role === fallback.role &&
          current.email === fallback.email &&
          current.name === fallback.name
        ) {
          return current;
        }

        return fallback;
      });
      return;
    }

    if (authUser && (activeRestaurantId || sessionTenant?.id)) {
      setStaffInfo((current) =>
        current ??
          buildFallbackStaffInfo({
            userId: sessionUser?.id ?? authUser.id,
            email: sessionUser?.email ?? authUser.email,
            restaurantId: sessionTenant?.id ?? activeRestaurantId ?? null,
            restaurantName: activeRestaurantName,
            role: normalizedRole,
          }),
      );
    }
  }, [
    activeRestaurantId,
    activeRestaurantName,
    authUser,
    sessionAuthenticated,
    sessionLoading,
    sessionPermissions,
    sessionTenant,
    sessionUser,
    clearTimeoutRef,
  ]);

  useEffect(() => {
    if (!sessionAuthenticated || !authUser || !sessionTenant) {
      return;
    }

    if (staffFetchInFlightRef.current) {
      return;
    }

    if (!isFallbackStaff(staffInfo)) {
      return;
    }

    let cancelled = false;
    staffFetchInFlightRef.current = true;

    clearTimeoutRef();
    timeoutRef.current = setTimeout(() => {
      if (cancelled) {
        return;
      }

      setStaffInfo((current) => {
        if (current) {
          return current;
        }

        return buildFallbackStaffInfo({
          userId: sessionUser?.id ?? authUser.id,
          email: sessionUser?.email ?? authUser.email,
          restaurantId: sessionTenant.id,
          restaurantName: activeRestaurantName,
          role,
        });
      });
    }, ROLE_TIMEOUT_MS);

    loadStaffInfo()
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (result) {
          const normalizedRole = deriveRole(result.role, true);
          const normalizedPermissions = mergePermissions(normalizedRole, sessionPermissions, true);

          setStaffInfo({
            ...result,
            role: normalizedRole,
          });
          setRole(normalizedRole);
          setPermissions(normalizedPermissions);
        }
      })
      .catch((error) => {
        console.error('Error fetching staff info:', error);
      })
      .finally(() => {
        staffFetchInFlightRef.current = false;
        if (!cancelled) {
          clearTimeoutRef();
        }
      });

    return () => {
      cancelled = true;
      clearTimeoutRef();
    };
  }, [
    activeRestaurantName,
    authUser,
    loadStaffInfo,
    role,
    sessionAuthenticated,
    sessionPermissions,
    sessionTenant,
    sessionUser,
    staffInfo,
    clearTimeoutRef,
  ]);

  const refreshStaffInfo = useCallback(async () => {
    if (!authUser) {
      setStaffInfo(null);
      return;
    }

    try {
      const result = await loadStaffInfo();
      if (result) {
        const normalizedRole = deriveRole(result.role, Boolean(sessionTenant));
        const normalizedPermissions = mergePermissions(normalizedRole, sessionPermissions, Boolean(sessionTenant));

        setStaffInfo({
          ...result,
          role: normalizedRole,
        });
        setRole(normalizedRole);
        setPermissions(normalizedPermissions);
      }
    } catch (error) {
      console.error('Error refreshing staff info:', error);
    }
  }, [authUser, loadStaffInfo, sessionPermissions, sessionTenant]);

  const effectiveRole = staffInfo?.role ?? role;

  const hasRole = useCallback(
    (requiredRole: StaffRole | StaffRole[]): boolean => {
      if (!effectiveRole) {
        return false;
      }

      if (Array.isArray(requiredRole)) {
        return requiredRole.includes(effectiveRole);
      }

      return effectiveRole === requiredRole;
    },
    [effectiveRole],
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (effectiveRole === 'PLATFORM_ADMIN') {
        return true;
      }

      return permissions.includes(permission);
    },
    [effectiveRole, permissions],
  );

  const loading = authLoading || sessionLoading || !roleResolved;

  return {
    staffInfo,
    role: effectiveRole,
    permissions,
    loading,
    hasRole,
    hasPermission,
    refreshStaffInfo,
    isPlatformAdmin: hasRole('PLATFORM_ADMIN'),
    isOwner: hasRole('OWNER'),
    isManager: hasRole('MANAGER'),
    isCashier: hasRole('CASHIER'),
    isCook: hasRole('COOK'),
    isExpo: hasRole('EXPO'),
    isSupport: hasRole('SUPPORT'),
    // Backwards compatibility helpers
    isKitchen: hasRole('COOK'),
    isWaiter: hasRole('EXPO'),
  };
};


